import type { AlertDialog } from '@base-ui/react';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Brand } from '~/types/user';

import { createAppColumnHelper } from '~/components';
import { ColumnIds } from '~/constants/table';

import { BrandColorCell } from './BrandsTableCells/BrandColorCell';
import { BrandEmailCell } from './BrandsTableCells/BrandEmailCell';
import { BrandLogoCell } from './BrandsTableCells/BrandLogoCell';
import { BrandMobileCell } from './BrandsTableCells/BrandMobileCell';
import { BrandNameCell } from './BrandsTableCells/BrandNameCell';
import { BrandOptionsCell } from './BrandsTableCells/BrandOptionsCell';

const columnHelper = createAppColumnHelper<Brand>();

export type DialogHandles = Record<'deleteBrandHandle', AlertDialog.Handle<Brand>>;

export const useBrandsTableColumns = ({ dialogHandles }: { dialogHandles: DialogHandles }) => {
  const { t } = useTranslation(['common', 'settings']);

  return useMemo(
    () =>
      columnHelper.columns([
        // Logo
        columnHelper.display({
          id: ColumnIds.brandsLogo,
          header: t('settings:profile.brands.headers.logo'),
          cell: ({ row: { original: brand } }) => <BrandLogoCell brand={brand} />,
          meta: {
            styles: { size: '100px', minSize: 50 },
            isResizable: true,
          },
        }),

        // Company name
        columnHelper.accessor('companyName', {
          id: ColumnIds.brandsName,
          header: t('settings:profile.brands.headers.name'),
          cell: ({ row: { original: brand } }) => <BrandNameCell brand={brand} />,
          meta: {
            styles: { size: '1fr', minSize: 150 },
            isResizable: true,
          },
        }),

        // Email
        columnHelper.accessor('email', {
          id: ColumnIds.brandsEmail,
          header: t('settings:profile.brands.headers.email'),
          cell: ({ getValue }) => <BrandEmailCell email={getValue()} />,
          meta: {
            styles: { size: '1fr', minSize: 150 },
            isResizable: true,
          },
        }),

        // Color
        columnHelper.display({
          id: ColumnIds.brandsColor,
          header: t('settings:profile.brands.headers.color'),
          cell: ({ row: { original: brand } }) => <BrandColorCell brand={brand} />,
          meta: {
            styles: { size: 'max-content', minSize: 100, justify: 'center' },
          },
        }),

        // Options
        columnHelper.display({
          id: ColumnIds.options,
          cell: ({ row: { original: brand } }) => <BrandOptionsCell brand={brand} dialogHandles={dialogHandles} />,
          meta: {
            styles: { size: 'max-content', minSize: 40 },
          },
        }),

        // Mobile
        columnHelper.display({
          id: ColumnIds.brandsMobile,
          cell: ({ row: { original: brand } }) => <BrandMobileCell brand={brand} dialogHandles={dialogHandles} />,
          meta: { mobile: true },
        }),
      ]),
    [t, dialogHandles],
  );
};
